import {
    ToDoNotification,
    isTaskToDoNotification,
    ToDoNotificationOperationType,
} from 'owa-todo-notifications';
import { assertNever } from 'owa-assert';
import { getTodoCache } from '../todoCache';
import { toTodo, hasTeamsSource } from 'owa-todo-service';
import type { Todo } from 'owa-todo-types';

export function handleNotification(notification: ToDoNotification) {
    if (!isTaskToDoNotification(notification)) {
        return;
    }
    const { Operation, Payload, Id } = notification;
    switch (Operation) {
        case ToDoNotificationOperationType.Updated:
            const updatedTodo = Payload && !hasTeamsSource(Payload) ? toTodo(Payload) : null;
            if (updatedTodo) {
                // The To Do API returns 3 sequential notifications when dragging a task between lists -
                // the first is an update for the moved task (with the original parent folder ID,
                // which causes a transient UI bug where the moved task briefly reappears in the
                // original list and is why we discard it in this conditional). It is
                // followed by create and delete notifications, which correctly update the UI.
                let todoCache = getTodoCache();
                if (todoCache.selectItemById(Id)?.ParentFolderId === Payload?.ParentFolderId) {
                    todoCache.updateItem(Id, updatedTodo);
                }
            }
            break;
        case ToDoNotificationOperationType.Created:
            const addedTodo = Payload && !hasTeamsSource(Payload) ? toTodo(Payload) : null;
            // If the task creation notification is for a task present in the cache as a local lie,
            // then this task was created in OWA. We skip this notification to avoid duplication.
            if (addedTodo && !isLocalLieInCache(addedTodo)) {
                getTodoCache().addItem(addedTodo);
            }
            break;
        case ToDoNotificationOperationType.Deleted:
            getTodoCache().deleteItem(Id);
            break;
        default:
            assertNever(Operation);
    }
}

/**
 * This checks if a todo from the server exists in the cache as a local lie
 * @param todo a todo from a task notification
 */
function isLocalLieInCache(todo: Todo) {
    return getTodoCache().selectItemById(todo.CreatedWithLocalId) !== undefined;
}
